/**
 * Milestone attachment / document link allowlist for SharePoint on adobe.sharepoint.com.
 * Add or remove site path segments here (lowercase); matching uses full path segments only.
 *
 * Supported URL shapes per slug (built automatically):
 * - https://adobe.sharepoint.com/sites/{slug}/…
 * - https://adobe.sharepoint.com/:x:/s/{slug}/… (sharing links)
 * INTL Finance also allows /r/sites/{slug}/… for :p:/r/sites/… links.
 */

function escapeRegexSegment(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** /sites/{slug}/ and /s/{slug}/ (SharePoint "short" site in shared links) */
function sitesAndShortSitePattern(slug: string): string {
  const e = escapeRegexSegment(slug.toLowerCase());
  return `(?:^|\\/)sites\\/${e}(?:\\/|$)|(?:^|\\/)s\\/${e}(?:\\/|$)`;
}

/** INTL Finance: slug base + optional trailing "e" (dmeintlfinanc vs dmeintlfinance) */
function intlFinancePattern(baseSlug: string): string {
  const e = escapeRegexSegment(baseSlug.toLowerCase());
  return `(?:^|\\/)sites\\/${e}(?:e)?(?:\\/|$)|(?:^|\\/)s\\/${e}(?:e)?(?:\\/|$)|(?:^|\\/)r\\/sites\\/${e}(?:e)?(?:\\/|$)`;
}

export const milestoneLinkAllowlist = {
  adobeSharePointHostname: 'adobe.sharepoint.com',

  sharePointSiteSlugs: {
    /** DMESLC Review Artifacts */
    dmeslcReviewArtifacts: ['dmeslcreviewartifacts'],
    /** Pricing Ops site collections */
    pricingOperations: ['dmepricingops', 'pricingoperations'],
    /**
     * DMe INTL Finance — stored without optional trailing `e`;
     * both dmeintlfinanc and dmeintlfinance match.
     */
    intlFinanceSlugBaseWithOptionalE: 'dmeintlfinanc',
    /** Creative Cloud GTM site collection */
    creativeCloudGtm: ['creativecloudgtm']
  },

  /** Shown when a milestone link fails domain/path validation */
  userFacingErrorMessage:
    'Links must be from approved SharePoint locations (DMESLC Review Artifacts, DMEPricingOps, Pricing Operations, DMeINTL Finance, Creative Cloud GTM), Adobe Experience, Frame.io (e.g. https://next.frame.io/ or https://f.io/xxx), or Figma (e.g. https://figma.com/)'
} as const;

let compiledSharePointPathRegex: RegExp | null = null;

function getAdobeSharePointPathRegex(): RegExp {
  if (compiledSharePointPathRegex) {
    return compiledSharePointPathRegex;
  }

  const { sharePointSiteSlugs } = milestoneLinkAllowlist;
  const parts: string[] = [];

  for (const slug of sharePointSiteSlugs.dmeslcReviewArtifacts) {
    parts.push(sitesAndShortSitePattern(slug));
  }
  for (const slug of sharePointSiteSlugs.pricingOperations) {
    parts.push(sitesAndShortSitePattern(slug));
  }
  parts.push(
    intlFinancePattern(sharePointSiteSlugs.intlFinanceSlugBaseWithOptionalE)
  );
  for (const slug of sharePointSiteSlugs.creativeCloudGtm) {
    parts.push(sitesAndShortSitePattern(slug));
  }

  compiledSharePointPathRegex = new RegExp(parts.join('|'));
  return compiledSharePointPathRegex;
}

/** True if pathname is under an allowed SharePoint site segment. */
export function matchesAdobeSharePointMilestonePath(pathname: string): boolean {
  return getAdobeSharePointPathRegex().test(pathname.toLowerCase());
}
